// Doctor / admin only — the clinical snapshot for one assessment.
//
// Participants keep worksheet 34's dashboard. This view exists because that one
// answers "what are my scores" while a consultation needs "who is this, what
// must not be missed, where is the imbalance, what changed, did they do it".
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, Download, FileText, Printer, Stethoscope, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Section, Score, Delta, Label } from "@/components/clinical/kit";
import RedFlagBand from "@/components/clinical/RedFlagBand";
import IdentityStrip from "@/components/clinical/IdentityStrip";
import DomainGroups from "@/components/clinical/DomainGroups";
import ChangePanel from "@/components/clinical/ChangePanel";
import MeasuresPanel from "@/components/clinical/MeasuresPanel";
import AdherencePanel from "@/components/clinical/AdherencePanel";
import CompletenessBar from "@/components/clinical/CompletenessBar";
import {
  adherenceTable, adherenceTone, collectFlags, domainChanges, presentStages, tickedFrom,
} from "@/config/clinical";
import { interpret } from "@/lib/status";
import { exportCsv } from "@/lib/exportCsv";

function HeadStat({ label, children, sub }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-[#E5DFD3] bg-white px-4 py-2.5">
      <Label>{label}</Label>
      <div className="leading-tight">{children}</div>
      {sub && <div className="text-[11px] text-stone-500">{t(sub)}</div>}
    </div>
  );
}

export default function ClinicalSnapshotPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [consult, setConsult] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/assessments/${id}/clinical`)
      .then((r) => { setD(r.data); setConsult(r.data.assessment?.consultation?.notes || ""); })
      .catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  }, [id]);

  const openWorksheet = useCallback((wid) => nav(`/assessment/${id}?w=${wid}`), [id, nav]);

  const view = useMemo(() => {
    if (!d) return null;
    const stage = d.assessment?.stage;
    const worksheetsByStage = Object.fromEntries(
      Object.entries(d.stage_meta || {}).map(([s, m]) => [s, m.worksheets || {}]));
    const overallByStage = Object.fromEntries(
      Object.entries(d.stage_meta || {}).map(([s, m]) => [s, m.overall]));
    const flags = collectFlags(tickedFrom(d.current), (wid, i) => d.current?.[wid]?.data?.[`note_${i}`]);
    const changes = domainChanges(d.by_stage);
    const stages = presentStages(d.by_stage);
    // the scored stage immediately before this one, or null at the first stage
    const si = stages.indexOf(stage);
    const prevStage = si > 0 ? stages[si - 1] : null;
    const overallDelta = prevStage != null && overallByStage[stage] != null && overallByStage[prevStage] != null
      ? Math.round((overallByStage[stage] - overallByStage[prevStage]) * 10) / 10 : null;
    return {
      stage, worksheetsByStage, overallByStage, flags, changes, stages, prevStage, overallDelta,
      adherence: adherenceTable(worksheetsByStage),
      urgent: flags.filter((f) => f.tier === "urgent").length,
    };
  }, [d]);

  const saveConsult = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/assessments/${id}/consultation`, { notes: consult });
      setD((x) => ({ ...x, assessment: { ...x.assessment, consultation: data.consultation } }));
      toast.success(t("Consultation saved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const exportSnapshot = () => {
    const columns = [
      { label: "Worksheet", key: "num" }, { label: "Domain", key: "label" },
      ...view.stages.map((s) => ({ label: s, key: s })),
      { label: "Change", key: "total" }, { label: "Interpretation", key: "interp" },
    ];
    const rows = view.changes.map((c) => ({
      num: c.num, label: c.label, total: c.total ?? "",
      interp: interpret(c.now).label,
      ...Object.fromEntries(view.stages.map((s) => [s, d.by_stage[s]?.[c.wid] ?? ""])),
    }));
    rows.push({
      num: "", label: "OVERALL", total: view.overallDelta ?? "", interp: "",
      ...Object.fromEntries(view.stages.map((s) => [s, view.overallByStage[s] ?? ""])),
    });
    for (const f of view.flags)
      rows.push({ num: f.worksheetNum, label: `RED FLAG (${f.tier}): ${f.label}`, total: "", interp: f.note || "" });
    exportCsv(`Clinical - ${d.participant?.name || "participant"} - ${d.assessment?.stage}`, columns, rows);
  };

  if (err) return (
    <AppShell>
      <p className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="clinical-error">{err}</p>
      <button onClick={() => nav("/submissions")} className="mt-3 text-sm text-emerald-700 hover:underline">
        {t("Back to assessments")}
      </button>
    </AppShell>
  );
  if (!d || !view) return <AppShell><p className="text-sm text-stone-400">{t("Loading…")}</p></AppShell>;

  const it = interpret(view.overallByStage[view.stage]);
  const adhTone = adherenceTone(view.adherence.mean);
  const consultation = d.assessment?.consultation || {};

  return (
    <AppShell>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <button onClick={() => nav(user?.role === "doctor" ? "/panel" : "/submissions")}
            data-testid="clinical-back"
            className="mb-1 flex items-center gap-1 text-xs text-stone-500 hover:text-emerald-700">
            <ChevronLeft className="h-3 w-3" /> {t(user?.role === "doctor" ? "My caseload" : "All assessments")}
          </button>
          <h1 className="font-serif text-3xl font-bold text-stone-900" data-testid="clinical-title">
            {d.participant?.name || d.assessment?.participant_name}
          </h1>
          <p className="text-sm text-stone-500">
            {t("Clinical snapshot")} · {t(d.assessment?.stage)} · {d.assessment?.title}
            {d.participant?.participant_code && <> · <span className="font-mono">{d.participant.participant_code}</span></>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={exportSnapshot} data-testid="clinical-export"
            className="flex items-center gap-1.5 rounded-md border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50">
            <Download className="h-4 w-4" /> {t("Export")}
          </button>
          <button onClick={() => window.open(`/print/${id}`, "_blank")} data-testid="clinical-print"
            className="flex items-center gap-1.5 rounded-md border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50">
            <Printer className="h-4 w-4" /> {t("Print")}
          </button>
          <button onClick={() => nav(`/assessment/${id}`)} data-testid="clinical-open-worksheets"
            className="flex items-center gap-1.5 rounded-md bg-emerald-800 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-900">
            <FileText className="h-4 w-4" /> {t("Open worksheets")}
          </button>
        </div>
      </div>

      {/* headline figures */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <HeadStat label="Overall domain average" sub={it.label}>
          <div className="flex items-baseline gap-2">
            <Score value={view.overallByStage[view.stage]} color={it.color} size="lg" />
            <Delta value={view.overallDelta} testid="head-overall-delta" />
          </div>
        </HeadStat>
        <HeadStat label="Red flags" sub={view.urgent ? "Urgent items present" : "No urgent items"}>
          <div className="flex items-baseline gap-1.5">
            <span className="font-serif text-3xl font-bold tabular-nums"
              style={{ color: view.flags.length ? (view.urgent ? "#DC2626" : "#D97706") : "#15803D" }}
              data-testid="head-flag-count">
              {view.flags.length}
            </span>
            {view.urgent > 0 && <AlertTriangle className="h-4 w-4 text-rose-600" />}
          </div>
        </HeadStat>
        <HeadStat label="Mean adherence" sub={view.adherence.mean != null ? adhTone.label : "No review filled in"}>
          <span className="font-serif text-3xl font-bold tabular-nums" style={{ color: adhTone.color }}>
            {view.adherence.mean ?? "—"}
          </span>
        </HeadStat>
        <HeadStat label="Last consultation"
          sub={consultation.doctor_name ? `by ${consultation.doctor_name}` : "Not recorded"}>
          <span className="text-sm font-medium text-stone-800">
            {consultation.updated_at ? new Date(consultation.updated_at).toLocaleDateString() : "—"}
          </span>
        </HeadStat>
      </div>

      <div className="space-y-4">
        <RedFlagBand flags={view.flags} stage={view.stage} />

        <Section title="Consultation note" sub="Saved against this assessment" testid="clinical-consult"
          right={consultation.updated_at && (
            <span className="text-xs text-stone-500">
              {t("Last saved")} {new Date(consultation.updated_at).toLocaleString()}
              {consultation.doctor_name && ` · ${consultation.doctor_name}`}
            </span>
          )}>
          <textarea value={consult} onChange={(e) => setConsult(e.target.value)} rows={4}
            data-testid="clinical-consult-notes"
            placeholder={t("Clinical observations, referral made, dinacharya and aushadha plan…")}
            className="w-full rounded-md border border-[#E5DFD3] bg-white p-3 text-sm text-stone-800 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600/40" />
          <button onClick={saveConsult} disabled={saving} data-testid="clinical-consult-save"
            className="mt-2 flex items-center gap-1.5 rounded-md bg-emerald-800 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-900 disabled:opacity-50">
            <Stethoscope className="h-4 w-4" /> {saving ? t("Saving…") : t("Save notes")}
          </button>
        </Section>

        <CompletenessBar responses={d.current} onOpenWorksheet={openWorksheet} />
        <IdentityStrip responses={d.current} participant={d.participant} />
        <DomainGroups scores={d.by_stage?.[view.stage] || {}} responses={d.current} changes={view.changes} />
        <ChangePanel byStage={d.by_stage} changes={view.changes} overallByStage={view.overallByStage} />
        <MeasuresPanel worksheetsByStage={view.worksheetsByStage} />
        <AdherencePanel worksheetsByStage={view.worksheetsByStage} />
      </div>

      <p className="mt-6 flex items-center gap-1.5 text-xs text-stone-400">
        <Users className="h-3.5 w-3.5" />
        {t("This view is visible to doctors and admins only. Participants see the worksheet 34 dashboard.")}
      </p>
    </AppShell>
  );
}
